import { useEffect, useMemo, useState } from 'react';

import type { GroupEditValues } from '../../domain/groupRules';
import type { UseGroupingResult } from '../../hooks/useGrouping';

export interface GroupEditDraftValues extends GroupEditValues {
  deleteGroup: boolean;
}

export interface UseGroupEditDraftResult {
  addMembers: (ids: string[]) => void;
  draft: GroupEditDraftValues | null;
  original: GroupEditValues | null;
  removeMembers: (ids: string[]) => void;
  setColor: (color: string) => void;
  setName: (name: string) => void;
  toggleDeleteGroup: () => void;
}

const EMPTY_DRAFT = null;

/** Local, discardable edits of one group; reset whenever another group is picked. */
export function useGroupEditDraft(grouping: UseGroupingResult, groupId: string | null): UseGroupEditDraftResult {
  const group = grouping.editableGroups.find(({ id }) => id === groupId);
  const memberKey = groupId ? grouping.getMemberIds(groupId).join(',') : '';

  const original = useMemo<GroupEditValues | null>(
    () => (group ? { color: group.color, memberIds: memberKey ? memberKey.split(',') : [], name: group.name } : null),
    [group, memberKey]
  );

  const [draft, setDraft] = useState<GroupEditDraftValues | null>(EMPTY_DRAFT);

  useEffect(() => {
    setDraft(original ? { ...original, deleteGroup: false } : EMPTY_DRAFT);
  }, [original]);

  const update = (patch: (current: GroupEditDraftValues) => Partial<GroupEditDraftValues>) =>
    setDraft(current => (current ? { ...current, ...patch(current) } : current));

  return {
    addMembers: ids =>
      update(({ memberIds }) => ({ memberIds: [...memberIds, ...ids.filter(id => !memberIds.includes(id))] })),
    draft,
    original,
    removeMembers: ids => update(({ memberIds }) => ({ memberIds: memberIds.filter(id => !ids.includes(id)) })),
    setColor: color => update(() => ({ color })),
    setName: name => update(() => ({ name })),
    toggleDeleteGroup: () => update(({ deleteGroup }) => ({ deleteGroup: !deleteGroup }))
  };
}
